import type {
  DeliveryPolicyReason,
  IosPushPolicyStatus,
} from "@reminder-tracker/backend-contracts";
import { useMutation, useQuery } from "convex/react";
import { useEffect, useRef, useState } from "react";
import {
  getDeliveryCapabilities,
  getIosPushPolicy,
  setIosPushPolicy,
} from "../../backend/functions";
import { formatAdministrationTime } from "../../ui/administration-time";
import { ChannelPolicyReview } from "./channel-policy-review";

type Mode = "disabled" | "test-only";
type Feedback = Readonly<{ kind: "error" | "success"; message: string }>;
type Command = Readonly<{
  commandKey: string;
  correlationId: string;
  expectedRevision: number | null;
  mode: Mode;
  reason: DeliveryPolicyReason;
}>;
const opaqueKey = (prefix: "cmd" | "corr"): string =>
  `${prefix}_${globalThis.crypto.randomUUID().replaceAll("-", "")}`;

function failureCode(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("data" in error)) return null;
  const data = error.data;
  if (typeof data !== "object" || data === null || !("code" in data)) return null;
  return typeof data.code === "string" ? data.code : null;
}

export function ConnectedIosPushGovernance(): React.JSX.Element {
  const [correlationId] = useState(() => opaqueKey("corr"));
  const status = useQuery(getIosPushPolicy, { correlationId });
  const capabilities = useQuery(getDeliveryCapabilities, { correlationId });
  const setPolicy = useMutation(setIosPushPolicy);
  const retained = useRef<Command | null>(null);
  const inFlight = useRef(false);
  const lifetime = useRef(0);
  const [pendingMode, setPendingMode] = useState<Mode | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [securityFailure, setSecurityFailure] = useState<{ error: unknown } | null>(null);
  useEffect(() => {
    // React Activity disconnects effects while keeping same-principal state.
    // A response from that earlier connection is uncertain, not a confirmed write.
    if (inFlight.current) {
      inFlight.current = false;
      setPendingMode(null);
      setUncertain(retained.current !== null);
      setFeedback({
        kind: "error",
        message:
          "The connection changed before this request was confirmed. Retry the same request to check its result.",
      });
    }
    return () => {
      lifetime.current++;
    };
  }, []);

  if (securityFailure !== null) throw securityFailure.error;
  if (status === undefined)
    return (
      <section
        className="push-governance push-governance-state"
        aria-busy="true"
        aria-live="polite"
      >
        <h2>iOS notifications</h2>
        <p>Checking the iOS sending policy…</p>
      </section>
    );
  const canManage = capabilities?.canManagePolicy === true;

  const submit = async (command: Command): Promise<void> => {
    if (inFlight.current || !canManage) return;
    const generation = lifetime.current;
    retained.current = command;
    inFlight.current = true;
    setPendingMode(command.mode);
    setFeedback(null);
    try {
      const result = await setPolicy(command);
      if (generation !== lifetime.current) return;
      retained.current = null;
      setUncertain(false);
      setFeedback({
        kind: "success",
        message: `iOS policy change recorded at revision ${result.revision}. The current policy is shown above.`,
      });
    } catch (error) {
      if (generation !== lifetime.current) return;
      const code = failureCode(error);
      if (
        code !== null &&
        [
          "AUTHENTICATION_REQUIRED",
          "AUTHORIZATION_DENIED",
          "REAUTHENTICATION_REQUIRED",
          "MFA_REQUIRED",
        ].includes(code)
      ) {
        retained.current = null;
        setSecurityFailure({ error });
      } else if (code !== null && ["CONFLICT", "INVALID_INPUT", "STALE_REVISION"].includes(code)) {
        retained.current = null;
        setUncertain(false);
        setFeedback({
          kind: "error",
          message: "The change was refused. Review the current policy before trying again.",
        });
      } else {
        setUncertain(true);
        setFeedback({
          kind: "error",
          message:
            "We could not confirm this change. Retry the same request to check its result before making another change.",
        });
      }
    } finally {
      if (generation === lifetime.current) {
        inFlight.current = false;
        setPendingMode(null);
      }
    }
  };

  return (
    <IosPushGovernanceView
      status={status}
      pendingMode={pendingMode}
      feedback={feedback}
      canManagePolicy={canManage}
      uncertain={uncertain}
      onRetry={() => {
        if (retained.current !== null) void submit(retained.current);
      }}
      onChangeMode={(mode, reason) => {
        if (retained.current !== null || inFlight.current || !canManage) return;
        void submit({
          commandKey: opaqueKey("cmd"),
          correlationId: opaqueKey("corr"),
          expectedRevision: status.policy.state === "configured" ? status.policy.revision : null,
          mode,
          reason,
        });
      }}
    />
  );
}

export function IosPushGovernanceView({
  status,
  pendingMode,
  feedback,
  canManagePolicy,
  uncertain,
  onRetry,
  onChangeMode,
}: Readonly<{
  status: IosPushPolicyStatus;
  pendingMode: Mode | null;
  feedback: Feedback | null;
  canManagePolicy: boolean;
  uncertain: boolean;
  onRetry: () => void;
  onChangeMode: (mode: Mode, reason: DeliveryPolicyReason) => void;
}>): React.JSX.Element {
  const policy = status.policy;
  const mode = policy.state === "configured" ? policy.mode : "disabled";
  return (
    <section className="push-governance" aria-labelledby="ios-push-title">
      <div className="section-head push-governance-head">
        <div>
          <h2 id="ios-push-title">iOS notifications</h2>
          <p>Control iOS sending separately from Android. In-app reminders stay available.</p>
        </div>
        <span className="push-environment">{status.environment}</span>
      </div>
      <div className="push-policy ios-push-policy">
        <div>
          <strong>{mode === "disabled" ? "Off" : "Test only"}</strong>
          {policy.state === "configured" ? (
            <small>
              Revision {policy.revision} ·{" "}
              <time dateTime={new Date(policy.updatedAtEpochMilliseconds).toISOString()}>
                {formatAdministrationTime(policy.updatedAtEpochMilliseconds)}
              </time>
            </small>
          ) : (
            <small>No iOS policy has been configured. Sending stays off.</small>
          )}
        </div>
        {uncertain ? (
          <button
            type="button"
            className="secondary-action"
            disabled={!canManagePolicy || pendingMode !== null}
            onClick={onRetry}
          >
            {pendingMode === null ? "Retry the same iOS policy request" : "Checking the request…"}
          </button>
        ) : (
          <ChannelPolicyReview
            channel="iOS push"
            environment={status.environment}
            currentMode={mode}
            revision={policy.state === "configured" ? policy.revision : null}
            pendingMode={pendingMode}
            canManagePolicy={canManagePolicy}
            onConfirm={onChangeMode}
          />
        )}
      </div>
      {feedback === null ? null : (
        <p
          className={`push-feedback ${feedback.kind}`}
          role={feedback.kind === "error" ? "alert" : "status"}
        >
          {feedback.message}
        </p>
      )}
      <p>
        This policy does not configure Apple credentials or device permissions. Test-only sending
        still requires the separately approved iOS delivery setup. Changing it does not alter
        Android settings or resend missed alerts.
      </p>
    </section>
  );
}
